import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { SignedIn, SignedOut, SignInButton, PricingTable, useSignIn } from '@clerk/clerk-react';

const CheckoutPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { signIn, setActive } = useSignIn();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const hasAttemptedAuth = useRef(false);

  // Handle Clerk sign-in token authentication from mobile app
  useEffect(() => {
    const token = searchParams.get('__clerk_ticket');
    
    // Only run when: token exists, signIn is loaded, not already authenticating, and haven't attempted yet
    if (token && signIn && !isAuthenticating && !hasAttemptedAuth.current) {
      hasAttemptedAuth.current = true; // Mark that we've attempted
      setIsAuthenticating(true);
      console.log('🔐 Clerk sign-in token detected, authenticating...');
      
      // Use Clerk's sign-in token to authenticate
      signIn.create({
        strategy: 'ticket',
        ticket: token,
      })
      .then((result) => {
        console.log('✅ Sign-in result:', result.status);
        
        if (result.status === 'complete' && result.createdSessionId) {
          // Set the active session
          return setActive({ session: result.createdSessionId });
        } else {
          throw new Error('Sign-in not complete');
        }
      })
      .then(() => {
        console.log('✅ Session activated');
        // Remove token from URL after successful auth
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('__clerk_ticket');
        setSearchParams(newSearchParams, { replace: true });
        setIsAuthenticating(false);
      })
      .catch((error) => {
        console.error('❌ Failed to authenticate with token:', error);
        setIsAuthenticating(false);
        // Remove token from URL even on error
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('__clerk_ticket');
        setSearchParams(newSearchParams, { replace: true });
      });
    }
  }, [signIn, searchParams, setSearchParams, setActive, isAuthenticating]); // Run when signIn becomes available

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white">
      {isAuthenticating && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-saverly-primary"></div>
              <p className="text-gray-700">Signing you in...</p>
            </div>
          </div>
        </div>
      )}
      {/* Hero Section */}
      <section className="pt-8 pb-8 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-saverly-dark font-poppins mb-3">
            Upgrade to{' '}
            <span className="text-saverly-primary">Saverly Pro</span>
          </h1>
          <p className="text-lg text-gray-600 font-inter">
            Unlock unlimited sales-powered recipes and import your own recipes
          </p>
        </div>
      </section>

      {/* Clerk Pricing Table - Shows for signed-in users */}
      <SignedIn>
        <section className="py-8 px-4">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-br from-saverly-primary/5 to-saverly-accent/5 px-8 py-6 border-b border-gray-100">
                <h2 className="text-2xl font-bold text-center text-saverly-dark font-poppins">
                  Complete Your Upgrade
                </h2>
              </div>
              <div className="p-8">
                <PricingTable 
                  afterPurchaseUrl="saverly://profile?refresh=true"
                />
              </div>
            </div>

            {/* Back to Pricing Link */}
            <div className="text-center mt-8">
              <Link 
                to="/pricing" 
                className="inline-flex items-center gap-2 text-saverly-primary hover:text-saverly-deep font-inter font-medium transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to pricing details
              </Link>
            </div>
          </div>
        </section>
      </SignedIn>

      {/* Sign In Prompt for non-authenticated users */}
      <SignedOut>
        <section className="py-8 px-4">
          <div className="max-w-md mx-auto">
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-10 text-center">
              <div className="mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-saverly-primary/10 to-saverly-accent/10 rounded-2xl mx-auto flex items-center justify-center">
                  <svg className="w-8 h-8 text-saverly-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
              </div>
              
              <h2 className="text-2xl font-bold text-saverly-dark font-poppins mb-3">
                Sign In to Continue
              </h2>
              <p className="text-gray-600 mb-8 font-inter">
                Please sign in to your Saverly account to complete your Pro subscription.
              </p>
              
              <SignInButton mode="modal">
                <Button className="w-full bg-saverly-primary hover:bg-saverly-deep text-white px-8 py-3 rounded-xl font-semibold shadow-sm transition-all">
                  Sign In to Subscribe
                </Button>
              </SignInButton>
              
              <div className="mt-8 pt-8 border-t border-gray-100">
                <p className="text-sm text-gray-600 mb-4 font-inter">Don't have an account yet?</p>
                <Link to="/download">
                  <Button className="w-full bg-white border-2 border-gray-200 text-gray-700 hover:border-saverly-primary hover:text-saverly-primary px-8 py-3 rounded-xl font-semibold transition-all">
                    Download Saverly
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </SignedOut>

      {/* Features Reminder */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <h3 className="text-3xl font-bold text-center text-saverly-dark font-poppins mb-12">
            What You Get with Pro
          </h3>
          
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            <div className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-saverly-primary/30 hover:shadow-md transition-all">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-saverly-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2 font-poppins">Unlimited Recipes</h4>
                  <p className="text-gray-600 text-sm font-inter leading-relaxed">Generate as many recipes as you want, whenever you want. No weekly limits.</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-saverly-primary/30 hover:shadow-md transition-all">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-50 to-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2 font-poppins">Import Your Own Recipes</h4>
                  <p className="text-gray-600 text-sm font-inter leading-relaxed">Paste any recipe from websites or cookbooks. We'll match ingredients to your local store.</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-saverly-primary/30 hover:shadow-md transition-all">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2 font-poppins">Priority Support</h4>
                  <p className="text-gray-600 text-sm font-inter leading-relaxed">Get faster response times and dedicated help when you need it.</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-saverly-primary/30 hover:shadow-md transition-all">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2 font-poppins">Cancel Anytime</h4>
                  <p className="text-gray-600 text-sm font-inter leading-relaxed">No contracts or commitments. Pause or cancel your subscription whenever you want.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-12 px-4 bg-white border-t border-gray-100">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap justify-center items-center gap-8 text-gray-600 text-sm font-inter">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-50 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <span className="font-medium">Secure Payment</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
              </div>
              <span className="font-medium">SSL Encrypted</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-50 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                  <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
                </svg>
              </div>
              <span className="font-medium">Instant Access</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CheckoutPage;

